import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import 'jsvectormap/dist/jsvectormap.min.css';
import Chart from 'react-apexcharts';
import Moment from 'moment';
import Select from 'react-select';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import useDarkMode from '../../hooks/useDarkMode';
import DropDownStyles, { DropDownStylesDark } from '../staff/dropdownStyles';
import * as bootstrap from 'bootstrap';

function Dashboard() {

	const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
	const { profileData } = useSelector((state) => state.auth);
	const isDarkMode = useDarkMode();
	const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

	const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
	const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
	const defaultLocation = useMemo(() => ({ value: 'all', label: 'All Locations' }), []);
	const parentIdForLocation = isMain && CompID != null ? (typeof CompID === 'number' ? CompID : parseInt(CompID, 10)) : null;
	const companyName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';

	const [compCurrency, setCompCurrency] = useState("GBP");
	const [loading, setLoading] = useState(false);
	const [salesData, setSalesData] = useState(null);
	const [topProducts, setTopProducts] = useState([]);
	const [topReportingCategories, setTopReportingCategories] = useState([]);
	const [hourlySalesData, setHourlySalesData] = useState([]);
	const [selectedDate, setSelectedDate] = useState(() => Moment());
	const [locations, setLocations] = useState([]);
	const [loadingLocations, setLoadingLocations] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState(defaultLocation);

	const selectedDateStr = selectedDate.format('YYYY-MM-DD');
	const profileCompCurrency = profileData?.compCurrency;
	const profileCompName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';

	const currencySymbols = {
		GBP: "£",
		USD: "$",
		EUR: "€",
	};

	const getCurrencySymbol = (currencyCode) => {
		return currencySymbols[currencyCode] || currencyCode;
	};

	const fetchLocations = useCallback(async () => {
		if (!isMain || !CompID) return;
		try {
			setLoadingLocations(true);
			const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
				params: { CompID },
			});
			const locationData = response.data?.data ?? response.data ?? [];
			const arr = Array.isArray(locationData) ? locationData : [];
			const parentId = typeof CompID === 'number' ? CompID : parseInt(CompID, 10);
			const base = [{ value: parentId, label: profileCompName }];
			const branchOpts = arr
				.filter((loc) => {
					const pid = loc.ParentComID ?? loc.parentComID;
					const id = loc.CompID ?? loc.compID;
					const pidNum = pid != null ? (typeof pid === 'number' ? pid : parseInt(pid, 10)) : null;
					const idNum = id != null ? (typeof id === 'number' ? id : parseInt(id, 10)) : null;
					if (idNum == null || isNaN(idNum)) return false;
					if (pidNum === parentId && idNum !== parentId) return true;
					return false;
				})
				.map((loc) => {
					const id = loc.CompID ?? loc.compID;
					const name = String(loc.CompName ?? loc.compName ?? '').trim() || `Branch ${id ?? ''}`;
					const val = typeof id === 'number' ? id : parseInt(id, 10);
					return { value: isNaN(val) ? null : val, label: name };
				})
				.filter((o) => o.value != null);
			setLocations([...base, ...branchOpts]);
		} catch (error) {
			console.error('Error fetching locations:', error);
			showErrorToast(error.response?.data?.message || 'Error loading locations');
		} finally {
			setLoadingLocations(false);
		}
	}, [CompID, isMain, profileCompName]);

	const locationOptions = useMemo(() => {
		if (locations.length > 0) return [defaultLocation, ...locations];
		if (parentIdForLocation != null && !isNaN(parentIdForLocation)) {
			return [defaultLocation, { value: parentIdForLocation, label: companyName }];
		}
		return [defaultLocation];
	}, [locations, parentIdForLocation, companyName, defaultLocation]);

	// For branch users: always filter to their own location (no location selector). For main: use selected location or null for "All".
	const locationFilterParam = useMemo(() => {
		if (!isMain) {
			// Branch user — force single location = own stats only
			const branchId = typeof CompID === 'number' ? CompID : parseInt(CompID, 10);
			return !isNaN(branchId) && branchId > 0 ? branchId : null;
		}
		if (selectedLocation?.value === 'all' || selectedLocation?.value == null) return null;
		const val = typeof selectedLocation.value === 'number' ? selectedLocation.value : parseInt(selectedLocation.value, 10);
		return isNaN(val) ? null : val;
	}, [isMain, selectedLocation?.value, CompID]);

	const loadDashboardData = useCallback(async () => {
		if (!CompID) return;

		setLoading(true);
		const params = {
			CompID,
			SelectedDate: selectedDateStr,
		};
		if (locationFilterParam != null) params.LocationFilter = locationFilterParam;

		try {
			const [salesRes, productsRes, categoriesRes, hourlyRes] = await Promise.all([
				axiosInstance.get(APIs.GET_TODAY_SALE, { params }),
				axiosInstance.get(APIs.GET_TOP_5_SOLD_PRODUCTS, { params }),
				axiosInstance.get(APIs.GET_TOP_5_REPORTING_CATEGORIES, { params }),
				axiosInstance.get(APIs.GET_HOURLY_SALES_FOR_CHART, { params }),
			]);

			if (salesRes.data?.status) {
				setSalesData(salesRes.data.data);
			} else {
				showErrorToast(salesRes.data?.message || "Failed to fetch today's sales data");
			}

			if (productsRes.data?.status) {
				setTopProducts(productsRes.data.data || []);
			} else {
				showErrorToast(productsRes.data?.message || "Failed to fetch top products data");
			}

			if (categoriesRes.data?.status) {
				setTopReportingCategories(categoriesRes.data.data || []);
			} else {
				showErrorToast(categoriesRes.data?.message || "Failed to fetch top reporting categories data");
			}

			if (hourlyRes.data?.status) {
				setHourlySalesData(hourlyRes.data.data || []);
			} else {
				showErrorToast(hourlyRes.data?.message || "Failed to fetch hourly sales data");
			}
		} catch (err) {
			showErrorToast("Failed to load dashboard data");
			console.error('Error fetching dashboard data:', err);
		} finally {
			setLoading(false);
		}
	}, [CompID, selectedDateStr, locationFilterParam]);

	// Helper function to format currency values
	const formatCurrency = (value) => {
		if (value === null || value === undefined || isNaN(value)) return `${getCurrencySymbol(compCurrency)}0.00`;
		return `${getCurrencySymbol(compCurrency)} ${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	// Fetch locations when parent company
	useEffect(() => {
		if (isMain) fetchLocations();
	}, [isMain, fetchLocations]);

	// Sync currency from profile without refetching dashboard APIs
	useEffect(() => {
		setCompCurrency(profileCompCurrency || "GBP");
	}, [profileCompCurrency]);

	// Single dashboard data load — only when CompID, date, or location filter changes
	useEffect(() => {
		loadDashboardData();
	}, [loadDashboardData]);

	const getSalesByTender = () => {
		if (salesData?.stats) {
			return [
				{ label: 'Cash', value: formatCurrency(salesData.stats.dS_SalesbyCash), change: '+0%', icon: '💷', orders: salesData.stats.dS_CashOrderCount !== undefined ? salesData.stats.dS_CashOrderCount : null },
				{ label: 'Card', value: formatCurrency(salesData.stats.dS_SalesByCard), change: '+0%', icon: '💳', orders: salesData.stats.dS_CardOrderCount !== undefined ? salesData.stats.dS_CardOrderCount : null },
				{ label: 'Online', value: formatCurrency(salesData.stats.dS_SalesByOnlineDeposit), change: '+0%', icon: '🌐', orders: salesData.stats.dS_SalesByOnlineDepositCount !== undefined ? salesData.stats.dS_SalesByOnlineDepositCount : null },
				{ label: 'Deposit', value: formatCurrency(salesData.stats.dS_SalesByDeposit), change: '+0%', icon: '🏦', orders: salesData.stats.dS_DepositOrderCount !== undefined ? salesData.stats.dS_DepositOrderCount : null },
			];
		}
		return [
			{ label: 'Cash', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '💷', orders: '0' },
			{ label: 'Card', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '💳', orders: '0' },
			{ label: 'Online', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '🌐', orders: '0' },
			{ label: 'Deposit', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '🏦', orders: '0' },
		];
	};

	const getSaleByCategory = () => {
		if (salesData) {
			const categories = [];

			if (salesData.reportCatStats && Array.isArray(salesData.reportCatStats)) {
				salesData.reportCatStats.forEach(category => {
					categories.push({
						label: category.categoryTypeName || 'Unknown Category',
						value: formatCurrency(category.totalSales),
						orders: category.orderCount || 0
					});
				});
			}

			if (salesData.menuStats) {
				categories.push({
					label: 'Menu',
					value: formatCurrency(salesData.menuStats.totalSales),
					orders: salesData.menuStats.orderCount || 0
				});
			}

			if (salesData.comboStats) {
				categories.push({
					label: 'Combo',
					value: formatCurrency(salesData.comboStats.totalSales),
					orders: salesData.comboStats.orderCount || 0
				});
			}

			return categories;
		}

		return [
			{ label: 'Menu', value: `${getCurrencySymbol(compCurrency)} 00.00`, orders: 0 },
			{ label: 'Combo', value: `${getCurrencySymbol(compCurrency)} 00.00`, orders: 0 },
		];
	};

	const getSaleByService = () => {
		if (salesData?.stats) {
			return [
				{ label: 'Dine In', value: formatCurrency(salesData.stats.dS_DineInSales), orders: salesData.stats.dS_DineInOrderCount || 0 },
				{ label: 'PICK UP', value: formatCurrency(salesData.stats.dS_TakeAwaySales), orders: salesData.stats.dS_TakeAwayOrderCount || 0 },
				{ label: 'Deliveries', value: formatCurrency(salesData.stats.dS_DeliveriesSales), orders: salesData.stats.dS_DeliveryOrderCount || 0 },
				{ label: 'Online Order', value: formatCurrency(salesData.stats.dS_OnlineOrderSales), orders: salesData.stats.dS_OnlineOrderCount || 0 },
			];
		}
		return [
			{ label: 'Dine In', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'PICK UP', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'Deliveries', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'Online Order', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
		];
	};

	const getSaleBy = () => {
		if (salesData?.stats) {
			return [
				{
					label: 'Voucher Discounts',
					value: formatCurrency(salesData.stats.dS_VoucherDiscount),
					orders: salesData.stats.dS_VoucherDiscountOrderCount !== undefined ? salesData.stats.dS_VoucherDiscountOrderCount : null,
					icon: 'url(/assets/img/icons/voucher.png)'
				},
				{
					label: 'Cancelled',
					value: formatCurrency(salesData.stats.dS_Void),
					orders: salesData.stats.dS_VoidOrderCount !== undefined ? salesData.stats.dS_VoidOrderCount : null,
					icon: 'url(/assets/img/icons/void.png)'
				},
				{
					label: 'Refund',
					value: formatCurrency(salesData.stats.dS_Refund),
					orders: salesData.stats.dS_RefundOrderCount !== undefined ? salesData.stats.dS_RefundOrderCount : null,
					icon: 'url(/assets/img/icons/refund.png)'
				},
				{
					label: 'Tip',
					value: formatCurrency(salesData.stats.dS_Tip),
					orders: null,
					icon: 'url(/assets/img/icons/tip.png)'
				},
				{
					label: 'Service Charges',
					value: formatCurrency(salesData.stats.dS_ServiceCharges),
					orders: null, // No order count data available
					icon: 'url(/assets/img/icons/discount.png)'
				},
				{
					label: 'Extra Charges',
					value: formatCurrency(salesData.stats.dS_ExtraCharges),
					orders: null,
					icon: 'url(/assets/img/icons/discount.png)'
				},
				{
					label: 'Delivery Charges',
					value: formatCurrency(salesData.stats.dS_DeliveryCharges),
					orders: null, // No order count data available
					icon: 'url(/assets/img/icons/discount.png)'
				},
			];
		}
		return [
			{ label: 'Voucher Discounts', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'Cancelled', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'Refund', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
			{ label: 'Tip', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
			{ label: 'Service Charges', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
			{ label: 'Extra Charges', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
			{ label: 'Delivery Charges', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
		];
	};

	function convertNumberWithCommas(x) {
		return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	};


	var blue = (getComputedStyle(document.body).getPropertyValue('--bs-blue')).trim();
	var red = (getComputedStyle(document.body).getPropertyValue('--bs-red')).trim();
	var orange = (getComputedStyle(document.body).getPropertyValue('--bs-orange')).trim();
	var lime = (getComputedStyle(document.body).getPropertyValue('--bs-lime')).trim();
	var cyan = (getComputedStyle(document.body).getPropertyValue('--bs-cyan')).trim();
	var teal = (getComputedStyle(document.body).getPropertyValue('--bs-teal')).trim();


	// Dynamic chart series based on hourly sales data
	const getHourlyChartSeries = () => {
		if (hourlySalesData.length === 0) {
			return [{
				name: 'Sales',
				data: []
			}, {
				name: 'Orders',
				data: []
			}];
		}

		return [{
			name: 'Sales',
			data: hourlySalesData.map(item => [item.hour, item.sales])
		}, {
			name: 'Orders',
			data: hourlySalesData.map(item => [item.hour, item.orderCount])
		}];
	};

	var visitorChartSeries = getHourlyChartSeries();

	var visitorChartOptions = {
		colors: ['#00acac', '#348fe2'],
		fill: { opacity: .75, type: 'solid' },
		legend: {
			position: 'top',
			horizontalAlign: 'right',
			offsetY: 15,
			offsetX: 500,
			labels: { colors: '#ffffff' }
		},
		xaxis: {
			type: 'numeric',
			tickAmount: 6,
			labels: {
				style: { colors: '#ffffff' },
				formatter: function (val) {
					const hour = parseInt(val);
					if (hour === 0) return '12 AM';
					if (hour < 12) return hour + ' AM';
					if (hour === 12) return '12 PM';
					return (hour - 12) + ' PM';
				}
			}
		},
		yaxis: { labels: { style: { colors: '#ffffff' } } },
		tooltip: {
			y: {
				formatter: function (val, opts) {
					const seriesName = opts.w.config.series[opts.seriesIndex].name;
					if (seriesName === 'Sales') {
						return getCurrencySymbol(compCurrency) + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
					} else {
						return val + ' orders';
					}
				}
			}
		},
		chart: { height: '100%', type: 'area', toolbar: { show: false }, stacked: true },
		plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
		dataLabels: { enabled: false },
		grid: {
			show: true, borderColor: 'rgba(255,255,255, .15)',
			xaxis: { lines: { show: true } },
			yaxis: { lines: { show: true } },
			padding: { top: -40, right: 3, bottom: 0, left: 10 }
		},
		stroke: { show: false, curve: 'straight' }
	};

	var conversionRateChartOptions = {
		chart: {
			type: 'line',
			width: 160,
			height: 28,
			sparkline: { enabled: true }
		},
		stroke: { curve: 'smooth', width: 3 },
		fill: {
			type: 'gradient',
			gradient: {
				opacityFrom: 1,
				opacityTo: 1,
				colorStops: [
					{ offset: 0, color: red, opacity: 1 },
					{ offset: 50, color: orange, opacity: 1 },
					{ offset: 100, color: lime, opacity: 1 }]
			},
		},
		labels: ['Jun 23', 'Jun 24', 'Jun 25', 'Jun 26', 'Jun 27', 'Jun 28', 'Jun 29'],
		xaxis: { crosshairs: { width: 1 } },
		tooltip: {
			theme: 'dark',
			fixed: { enabled: false },
			x: { show: false },
			y: {
				title: { formatter: function (seriesName) { return '' } },
				formatter: (value) => { return value + '%' },
			},
			marker: { show: false }
		},
		responsive: [
			{ breakpoint: 3000, options: { chart: { width: 120 } } },
			{ breakpoint: 1300, options: { chart: { width: 100 } } },
			{ breakpoint: 1200, options: { chart: { width: 160 } } },
			{ breakpoint: 900, options: { chart: { width: 120 } } },
			{ breakpoint: 576, options: { chart: { width: 180 } } },
			{ breakpoint: 400, options: { chart: { width: 120 } } }
		]
	};

	var conversionRateChartSeries = [{
		data: [2.68, 2.93, 2.04, 1.61, 1.88, 1.62, 2.80]
	}];

	var storeSessionChartOptions = {
		chart: {
			type: 'line',
			width: 160,
			height: 28,
			sparkline: { enabled: true },
			stacked: false
		},
		stroke: { curve: 'smooth', width: 3 },
		fill: {
			type: 'gradient',
			gradient: {
				opacityFrom: 1,
				opacityTo: 1,
				colorStops: [
					{ offset: 0, color: teal, opacity: 1 },
					{ offset: 50, color: blue, opacity: 1 },
					{ offset: 100, color: cyan, opacity: 1 }
				]
			},
		},
		labels: ['Jun 23', 'Jun 24', 'Jun 25', 'Jun 26', 'Jun 27', 'Jun 28', 'Jun 29'],
		xaxis: { crosshairs: { width: 1 } },
		tooltip: {
			theme: 'dark',
			fixed: { enabled: false },
			x: { show: false },
			y: {
				title: { formatter: function (seriesName) { return '' } },
				formatter: (value) => { return convertNumberWithCommas(value) },
			},
			marker: { show: false }
		},
		responsive: [
			{ breakpoint: 3000, options: { chart: { width: 120 } } },
			{ breakpoint: 1300, options: { chart: { width: 100 } } },
			{ breakpoint: 1200, options: { chart: { width: 160 } } },
			{ breakpoint: 900, options: { chart: { width: 120 } } },
			{ breakpoint: 576, options: { chart: { width: 180 } } },
			{ breakpoint: 400, options: { chart: { width: 120 } } }
		]
	};

	var storeSessionChartSeries = [{
		data: [10812, 11393, 7311, 6834, 9612, 11200, 13557]
	}];

	return (
		<>
			{loading && <Preloader />}
			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item"><Link to="/">Home</Link></li>
				<li className="breadcrumb-item active">Dashboard</li>
			</ol>
			<h1 className="page-header mb-3">Dashboard</h1>
			<div className="d-sm-flex align-items-center mb-3 gap-2 flex-wrap">
				<div className="d-flex align-items-center gap-2">
					<input
						id="date-picker"
						type="date"
						className="form-control"
						style={{ width: '200px' }}
						value={selectedDateStr}
						onChange={(e) => {
							const newDate = Moment(e.target.value);
							if (newDate.isValid()) {
								setSelectedDate(newDate);
							}
						}}
					/>
					{isMain && locationOptions.length > 1 && (
						<div style={{ minWidth: '200px' }}>
							<Select
								value={selectedLocation}
								onChange={(option) => setSelectedLocation(option || defaultLocation)}
								options={locationOptions}
								styles={dropdownStyles}
								isSearchable={false}
								isLoading={loadingLocations}
								placeholder="All Locations"
								className="dashboard-location-select"
								menuPortalTarget={document.body}
								menuPosition="fixed"
							/>
						</div>
					)}
				</div>
			</div>
			<div className="row">
				<div className="col-xl-6">
					<div className="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
						<div className="card-body">
							<div className="row">
								<div className="col-12">
									<div className="row">
										<div className="col-4">
											<div className="mb-3 text-gray-500">
												<b>Gross Sales</b>
												<span className="ms-2">
													<i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Gross Sales" data-bs-placement="top" data-bs-content="Total sales before deductions (amount received + discount)."></i>
												</span>
											</div>
											<div className="d-flex mb-1">
												<h2 className="mb-0">
													{salesData?.stats
														? formatCurrency(
															(salesData.stats.dS_GrossSales ?? 0)
														  )
														: `${getCurrencySymbol(compCurrency)} 00.00`
													}
												</h2>
											</div>
										</div>
										<div className="col-2"></div>

										<div className="col-4">
											<div className="mb-3 text-gray-500">
												<b>Net Sales</b>
												<span className="ms-2">
													<i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Net Sales" data-bs-placement="top" data-bs-content="Gross sales minus VAT (completed + refunded), Refund and Discount."></i>
												</span>
											</div>
											<div className="d-flex mb-1">
												<h2 className="mb-0">
													{salesData?.stats
														? formatCurrency(
															((salesData.stats.dS_GrossSales ?? 0)) -
															(((salesData.stats.dS_VATCollection ?? 0) + (salesData.stats.dS_VATRefunded ?? 0)) + (salesData.stats.dS_Refund ?? 0) + (salesData.stats.dS_Discount ?? 0))
														  )
														: `${getCurrencySymbol(compCurrency)} 00.00`
													}
												</h2>
											</div>
										</div>
									</div>

									<div className="small text-gray-500 mb-2 mt-2">Deductions (VAT, Refund, Discount)</div>
									<div className="row g-2 mb-2">
										<div className="col-md-4">
											<div className="text-gray-500">
												Total VAT {salesData?.stats
													? formatCurrency((salesData.stats.dS_VATCollection ?? 0) + (salesData.stats.dS_VATRefunded ?? 0))
													: `${getCurrencySymbol(compCurrency)} 00.00`
												}
											</div>
										</div>
										<div className="col-md-4">
											<div className="text-gray-500">
												Refund  {salesData?.stats?.dS_Refund !== undefined
													? formatCurrency(salesData.stats.dS_Refund)
													: `${getCurrencySymbol(compCurrency)} 00.00`
												}
												{salesData?.stats?.dS_RefundOrderCount !== undefined && salesData.stats.dS_RefundOrderCount > 0 && (
													<span className="ms-1">({salesData.stats.dS_RefundOrderCount})</span>
												)}
											</div>
										</div>
										<div className="col-md-4">
											<div className="text-gray-500">
												Discount  {salesData?.stats?.dS_Discount !== undefined
													? formatCurrency(salesData.stats.dS_Discount)
													: `${getCurrencySymbol(compCurrency)} 00.00`
												}
												{salesData?.stats?.dS_DiscountOrderCount !== undefined && salesData.stats.dS_DiscountOrderCount > 0 && (
													<span className="ms-1">({salesData.stats.dS_DiscountOrderCount})</span>
												)}
											</div>
										</div>
									</div>

									<hr className="bg-white-transparent-2" />
									<div className="row text-truncate">
										<div className="col-6">
											<div className="fs-12px text-gray-500">Avg. sales per order</div>
											<div className="fs-18px mb-5px fw-bold">
												{(() => {
													const gross = (salesData?.stats?.dS_GrossSales ?? 0) + (salesData?.stats?.dS_Refund ?? 0);
													const totalOrders = (salesData?.stats?.dS_TotalNumberofOrdersCount ?? 0) + (salesData?.stats?.dS_RefundOrderCount ?? 0);
													return totalOrders > 0 ? formatCurrency(gross / totalOrders) : `${getCurrencySymbol(compCurrency)} 00.00`;
												})()}
											</div>
											<div className="progress h-5px rounded-3 bg-gray-900 mb-5px">
												<div className="progress-bar progress-bar-striped rounded-right" style={{ width: '100%' }}></div>
											</div>
										</div>

										<div className="col-6">
											<div className="fs-12px text-gray-500">Total orders</div>
											<div className="fs-18px mb-5px fw-bold">
												{salesData?.stats
													? (
														(salesData.stats.dS_TotalNumberofOrdersCount ?? 0) +
														(salesData.stats.dS_RefundOrderCount ?? 0)
													  ).toLocaleString()
													: '0'
												}
											</div>
											<div className="progress h-5px rounded-3 bg-gray-900 mb-5px">
												<div className="progress-bar progress-bar-striped rounded-right bg-teal" style={{ width: '100%' }}></div>
											</div>
										</div>

									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-6">
					<div className="row">
						<div className="col-sm-6">
							<div className="card border-0 text-truncate mb-3 bg-gray-800 text-white">
								<div className="card-body">
									<div className="mb-3 text-gray-500">
										<b className="mb-3">
											SALES By
										</b>
										<span className="ms-2">
											<i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Total sales" data-bs-placement="top" data-bs-content="Gross Sales "></i>
										</span>
									</div>

									<div className="d-flex align-items-center mb-1">
										<h2 className="text-white mb-0"></h2>
										<div className="ms-auto">
											<Chart type="line" height={'28px'} options={conversionRateChartOptions} series={conversionRateChartSeries} />
										</div>
									</div>
									{/*
									<div className="mb-4 text-gray-500 ">
										<i className="fa fa-caret-down"></i> 0.50% compare to last week
									</div>
										*/}
									{/* Dynamic sales by tender data */}
									{getSalesByTender().map((tender, idx) => (
										<div key={idx} className={`d-flex ${idx === 0 ? 'mb-2 mt-2' : 'mb-2'}`}>
											<div className="d-flex align-items-center">
												<i className={`fa fa-circle fs-8px me-2 ${tender.label.includes('Card') ? 'text-red' :
													tender.label.includes('Cash') ? 'text-red' :
														tender.label.includes('Online') ? 'text-warning' :
															tender.label.includes('Deposit') ? 'text-lime' : 'text-gray'
													}`}></i>
												{tender.label}
											</div>
											<div className="d-flex align-items-center ms-auto">
												<div className="w-100px text-end ps-2 fw-bold">
													{tender.value} {tender.orders !== null && tender.orders !== undefined && `(${tender.orders})`}
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
						<div className="col-sm-6">
							<div className="card border-0 text-truncate mb-3 bg-gray-800 text-white">
								<div className="card-body">
									<div className="mb-3 text-gray-500">
										<b className="mb-3">Sales By Service</b>
										<span className="ms-2"><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Store Sessions" data-bs-placement="top" data-bs-content="Number of sessions on your online store. A session is a period of continuous activity from a visitor." data-original-title="" title=""></i></span>
									</div>
									<div className="d-flex align-items-center mb-1">
										<h2 className="text-white mb-0"></h2>
										<div className="ms-auto">
											<Chart type="line" height={'28px'} options={storeSessionChartOptions} series={storeSessionChartSeries} />
										</div>
									</div>
									{/* 
									<div className="mb-4 text-gray-500 ">
										<i className="fa fa-caret-up"></i> 9.5% compare to last week
									</div>
									*/}
									{/* Dynamic sales by service data */}
									{getSaleByService().map((service, idx) => (
										<div key={idx} className="d-flex mb-2">
											<div className="d-flex align-items-center">
												<i className={`fa fa-circle fs-8px me-2 ${service.label.includes('Dine In') ? 'text-teal' :
													service.label.includes('PICK UP') ? 'text-blue' :
														service.label.includes('Deliveries') ? 'text-aqua' :
															service.label.includes('Online Order') ? 'text-aqua' : 'text-gray'
													}`}></i>
												{service.label.toUpperCase()}
											</div>
											<div className="d-flex align-items-center ms-auto">
												<div className="w-100px text-end ps-2 fw-bold">
													{service.value} {service.orders !== null && service.orders !== undefined && `(${service.orders})`}
												</div>
											</div>
										</div>
									))}

								</div>
							</div>
						</div>
					</div>
				</div>
			</div>



			<div className="row">
				<div className="col-xl-8 col-lg-6">
					<div className="card border-0 mb-3 bg-gray-800 text-white">
						<div className="card-body">
							<div className="mb-3 text-gray-500">
								<b>HOURLY SALES ANALYTICS</b>
								<span className="ms-2"><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Hourly sales and orders" data-bs-placement="top" data-bs-content="Hourly breakdown of sales and order count for the selected date." data-original-title="" title=""></i></span>
							</div>

						</div>
						<div className="card-body p-0">
							<div style={{ height: '269px' }}>
								<div className="widget-chart-full-width pe-4" data-bs-theme="dark" style={{ height: '254px' }}>
									<Chart type="area" height="254" width="100%" options={visitorChartOptions} series={visitorChartSeries} />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card bg-gray-800 border-0 text-white mb-3">
						<div className="card-body">
							<div className="mb-2 text-grey">
								<b>OTHER STATS</b>
								<span className="ms-2"><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Total sales" data-bs-placement="top" data-bs-content="Net sales (gross sales minus discounts and returns) plus taxes and shipping. Includes orders from all sales channels."></i></span>
							</div>
							<div>

								{getSaleBy().map((stat, idx) => (
									<div className="d-flex mb-2">
										<div className="d-flex align-items-center">
											<i className={`fa fa-circle fs-8px me-2 
											${stat.label.includes('Discounts') ? 'text-teal' :
													stat.label.includes('Cancelled') ? 'text-blue' :
														stat.label.includes('Refund') ? 'text-danger' :
															stat.label.includes('Tip') ? 'text-aqua' :
																stat.label.includes('Service Charges') ? 'text-red' :
																	stat.label.includes('Extra Charges') ? 'text-orange' : 'text-warning'
												}`}></i>
											{stat.label}
										</div>
										<div className="d-flex align-items-center ms-auto">
											<div className="w-100px text-end ps-2 fw-bold">
												{stat.value} {stat.orders !== null && stat.orders !== undefined && `(${stat.orders})`}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 mb-3 bg-gray-900 text-white">
						<div className="card-body" style={{ background: 'no-repeat bottom right', backgroundImage: 'url(/assets/img/svg/img-4.svg)', backgroundSize: 'auto 60%' }}>
							<div className="mb-3 text-gray-500 ">
								<b>SALES BY Category</b>
								<span className="text-gray-500 ms-2"><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Sales by social source" data-bs-placement="top" data-bs-content="Total online store sales that came from a social referrer source."></i></span>
							</div>

						</div>
						<div className="widget-list rounded-bottom" data-bs-theme="dark">



							{getSaleByCategory().map((category, idx) => (
								<div key={idx} className="d-flex mb-2 p-2">
									<div className="d-flex align-items-center">
										<i className={`fa fa-circle fs-8px me-2 ${idx === 0 ? 'text-teal' :
											idx === 1 ? 'text-blue' :
												idx === 2 ? 'text-aqua' :
													idx === 3 ? 'text-red' :
														idx === 4 ? 'text-warning' :
															idx === 5 ? 'text-success' : 'text-gray'
											}`}></i>
										{category.label.toUpperCase()}
									</div>
									<div className="d-flex align-items-center ms-auto">
										<div className="w-100px text-end ps-2 fw-bold">
											{category.value} {category.orders !== null && category.orders !== undefined && `(${category.orders})`}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 mb-3 bg-gray-800 text-white">
						<div className="card-body">
							<div className="mb-3 text-gray-500">
								<b>TOP PRODUCTS BY UNITS SOLD</b>
								<span className="ms-2 "><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Top products with units sold" data-bs-placement="top" data-bs-content="Products with the most individual units sold. Includes orders from all sales channels."></i></span>
							</div>
							{topProducts.length > 0 ? (
								topProducts.map((product, idx) => (
									<div key={product.MenuItem_ID || idx} className={`d-flex align-items-center ${idx < topProducts.length - 1 ? 'mb-15px' : ''}`}>
										<div className="widget-img rounded-3 me-10px bg-white p-3px w-30px">
											<div className="h-100 w-100" style={{
												background: product.productImage
													? `url(${product.productImage}) center no-repeat`
													: 'url(/assets/img/product/product-8.jpg) center no-repeat',
												backgroundSize: 'auto 100%'
											}}></div>
										</div>
										<div className="text-truncate">
											<div>{product.productName}</div>
											<div className="text-gray-500">{formatCurrency(product.productPrice)}</div>
										</div>
										<div className="ms-auto text-center">
											<div className="fs-13px">{product.unitsSold}</div>
											<div className="text-gray-500 fs-10px">sold</div>
										</div>
									</div>
								))
							) : (
								<div className="text-center text-gray-500 py-4">
									<div>No products sold on this date</div>
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 mb-3 bg-gray-800 text-white">
						<div className="card-body">
							<div className="mb-3 text-gray-500">
								<b>TOP REPORTING CATEGORIES</b>
								<span className="ms-2 "><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Top reporting categories by sales" data-bs-placement="top" data-bs-content="Categories with the highest total sales. Shows performance by product categories."></i></span>
							</div>
							{topReportingCategories.length > 0 ? (
								topReportingCategories.map((category, idx) => (
									<div key={category.RootCat_ID || idx} className={`d-flex align-items-center ${idx < topReportingCategories.length - 1 ? 'mb-15px' : ''}`}>
										<div className="widget-img rounded-3 me-10px bg-white p-3px w-30px">
											<div className="h-100 w-100" style={{
												background: category.categoryImage
													? `url(${category.categoryImage}) center no-repeat`
													: 'url(/assets/img/product/product-8.jpg) center no-repeat',
												backgroundSize: 'auto 100%'
											}}></div>
										</div>
										<div className="text-truncate">
											<div>{category.categoryName}</div>
											<div className="text-gray-500">{category.totalOrders} orders</div>
										</div>
										<div className="ms-auto text-center">
											<div className="fs-13px">{category.totalProductsSold}</div>
											<div className="text-gray-500 fs-10px">items</div>
										</div>
									</div>
								))
							) : (
								<div className="text-center text-gray-500 py-4">
									<div>No categories with sales on this date</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* 
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 mb-3 bg-gray-800 text-white">
						<div className="card-body">
							<div className="mb-3 text-gray-500">
								<b>MARKETING CAMPAIGN</b>
								<span className="ms-2"><i className="fa fa-info-circle" data-bs-toggle="popover" data-bs-trigger="hover" data-bs-title="Marketing Campaign" data-bs-placement="top" data-bs-content="Campaign that run for getting more returning customers."></i></span>
							</div>
							<div className="row align-items-center pb-1px">
								<div className="col-4">
									<div className="h-100px d-flex align-items-center justify-content-center">
										<img src="/assets/img/svg/img-2.svg" alt="" className="mw-100 mh-100" />
									</div>
								</div>
								<div className="col-8">
									<div className="mb-2px text-truncate">Email Marketing Campaign</div>
									<div className="mb-2px  text-gray-500  fs-11px">Mon 12/6 - Sun 18/6</div>
									<div className="d-flex align-items-center mb-2px">
										<div className="flex-grow-1">
											<div className="progress h-5px rounded-pill bg-white bg-opacity-10">
												<div className="progress-bar progress-bar-striped bg-indigo" style={{ width: '80%' }}></div>
											</div>
										</div>
										<div className="ms-2 fs-11px w-30px text-center">80%</div>
									</div>
									<div className="text-gray-500 small mb-15px text-truncate">
										57.5% people click the email
									</div>
									<Link to="/dashboard/v3" className="btn btn-xs btn-indigo fs-10px ps-2 pe-2">View campaign</Link>
								</div>
							</div>
							<hr className="bg-white-transparent-2 mt-20px mb-20px" />
							<div className="row align-items-center">
								<div className="col-4">
									<div className="h-100px d-flex align-items-center justify-content-center">
										<img src="/assets/img/svg/img-3.svg" alt="" className="mw-100 mh-100" />
									</div>
								</div>
								<div className="col-8">
									<div className="mb-2px text-truncate">Facebook Marketing Campaign</div>
									<div className="mb-2px  text-gray-500  fs-11px">Sat 10/6 - Sun 18/6</div>
									<div className="d-flex align-items-center mb-2px">
										<div className="flex-grow-1">
											<div className="progress h-5px rounded-pill bg-white bg-opacity-10">
												<div className="progress-bar progress-bar-striped bg-warning" style={{ width: '60%' }}></div>
											</div>
										</div>
										<div className="ms-2 fs-11px w-30px text-center">60%</div>
									</div>
									<div className="text-gray-500 small mb-15px text-truncate">
										+124k visitors from facebook
									</div>
									<Link to="/dashboard/v3" className="btn btn-xs btn-warning fs-10px ps-2 pe-2">View campaign</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
				*/}
			</div>
		</>
	)
};

export default Dashboard;